import type { FormElement, NumberInputProperties } from '../../model/form.types';
import type { FieldDefinition, FieldValue, ValidationIssue } from '../../model/types';
import {
  compareDecimals,
  countDecimalPlaces,
  countDigits,
  DECIMAL_MAX_DIGITS,
  decimalFromStored,
  decimalPlacesMessage,
  effectiveDecimalPlaces,
  formatPtBrDecimal,
  INTEGER_MESSAGE,
  MAX_DIGITS_MESSAGE,
  numberRangeMessage,
} from '../decimal';
import { builderDefaultText, isEmptyValue, requiredIssue, singleColumn, typeIssue } from './shared';

type NumberInputElement = FormElement & { properties: NumberInputProperties };

function shapeIssue(element: NumberInputElement, value: FieldValue): ValidationIssue | null {
  return value === null || typeof value === 'number' || typeof value === 'string' ? null : typeIssue(element);
}

function limit(value: unknown): string | undefined {
  return decimalFromStored(value) ?? undefined;
}

function placesIssue(element: NumberInputElement, decimal: string): ValidationIssue | null {
  const { numberType, decimalPlaces } = element.properties;
  const allowedPlaces = numberType === 'integer' ? 0 : effectiveDecimalPlaces(numberType, decimalPlaces);
  if (allowedPlaces === undefined || countDecimalPlaces(decimal) <= allowedPlaces) return null;
  const message = allowedPlaces === 0 ? INTEGER_MESSAGE : decimalPlacesMessage(allowedPlaces);
  return { fieldId: element.id, code: 'regex', message };
}

function rangeIssue(element: NumberInputElement, decimal: string): ValidationIssue | null {
  const minValue = limit(element.properties.minValue);
  const maxValue = limit(element.properties.maxValue);
  const belowMin = minValue !== undefined && compareDecimals(decimal, minValue) < 0;
  const aboveMax = maxValue !== undefined && compareDecimals(decimal, maxValue) > 0;
  if (!belowMin && !aboveMax) return null;
  return { fieldId: element.id, code: belowMin ? 'min' : 'max', message: numberRangeMessage(minValue, maxValue) };
}

export const numberInputDefinition: FieldDefinition<NumberInputElement> = {
  kind: 'field',
  type: 'NUMBER_INPUT',
  emptyValue: (element) => decimalFromStored(builderDefaultText(element)),
  control: (element) => ({
    kind: 'number',
    placeholder: element.properties.placeholder,
    min: limit(element.properties.minValue),
    max: limit(element.properties.maxValue),
    numberType: element.properties.numberType,
    decimalPlaces: effectiveDecimalPlaces(element.properties.numberType, element.properties.decimalPlaces),
  }),
  shapeIssue,
  validate: (element, value) => {
    const shape = shapeIssue(element, value);
    if (shape) return [shape];
    if (isEmptyValue(value)) return element.properties.required ? [requiredIssue(element)] : [];
    const decimal = decimalFromStored(value);
    if (decimal === null) return [{ fieldId: element.id, code: 'regex', message: 'Informe um número.' }];
    if (countDigits(decimal) > DECIMAL_MAX_DIGITS) {
      return [{ fieldId: element.id, code: 'maxLength', message: MAX_DIGITS_MESSAGE }];
    }
    const issue = placesIssue(element, decimal) ?? rangeIssue(element, decimal);
    return issue ? [issue] : [];
  },
  columns: singleColumn,
  cells: (_element, value) => {
    const decimal = decimalFromStored(value);
    return [decimal === null ? '' : formatPtBrDecimal(decimal)];
  },
  triggerMatches: () => false,
};
